import { useEffect, useState } from 'react'

const EDITABLE_TAGS = ['INPUT', 'TEXTAREA'] as const

const NON_KEYBOARD_INPUT_TYPES = [
  'button',
  'submit',
  'reset',
  'checkbox',
  'radio',
  'range',
  'color',
  'file',
] as const

// Минимальная «съеденная» высота вьюпорта, при которой считаем клавиатуру открытой.
const KEYBOARD_MIN_HEIGHT_PX = 150

function isKeyboardEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  if (!EDITABLE_TAGS.includes(target.tagName as (typeof EDITABLE_TAGS)[number])) return false
  if (target instanceof HTMLInputElement) {
    return !NON_KEYBOARD_INPUT_TYPES.includes(
      target.type as (typeof NON_KEYBOARD_INPUT_TYPES)[number],
    )
  }
  return true
}

/** Закрывает экранную клавиатуру, снимая фокус с активного редактируемого элемента. */
export function hideKeyboard(): void {
  const active = document.activeElement
  if (active instanceof HTMLElement) active.blur()
}

/**
 * Возвращает true, когда открыта экранная клавиатура.
 * Детекция комбинированная: фокус на поле + изменение visualViewport.
 * Это важно, т.к. на iOS нативное скрытие клавиатуры НЕ снимает фокус с поля
 * (focusout не срабатывает) — без visualViewport навигация не возвращалась бы.
 */
export function useKeyboardOpen(): boolean {
  const [isOpen, setIsOpen] = useState(false)
  useEffect(() => {
    const viewport = window.visualViewport
    function handleFocusIn(event: FocusEvent): void {
      if (isKeyboardEditable(event.target)) setIsOpen(true)
    }
    function handleFocusOut(): void {
      // Откладываем проверку: фокус может переходить между соседними полями.
      window.setTimeout(() => {
        if (!isKeyboardEditable(document.activeElement)) setIsOpen(false)
      }, 0)
    }
    function handleViewportResize(): void {
      if (!viewport) return
      const keyboardHeight = window.innerHeight - viewport.height
      if (keyboardHeight < KEYBOARD_MIN_HEIGHT_PX) {
        // Клавиатура скрыта (в т.ч. нативной кнопкой, не снимающей фокус).
        setIsOpen(false)
      } else if (isKeyboardEditable(document.activeElement)) {
        setIsOpen(true)
      }
    }
    document.addEventListener('focusin', handleFocusIn)
    document.addEventListener('focusout', handleFocusOut)
    viewport?.addEventListener('resize', handleViewportResize)
    return () => {
      document.removeEventListener('focusin', handleFocusIn)
      document.removeEventListener('focusout', handleFocusOut)
      viewport?.removeEventListener('resize', handleViewportResize)
    }
  }, [])
  return isOpen
}
